import { CurrencyInput, CurrencyValueType } from "@cdkglobal/radial";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { PrimitiveValueType, RenderInputProps } from "../../../../types";
import { CurrencyInputWithTooltip } from "../../../common/Tooltip";
import { customEvaluations } from "../../evaluations";
import { uniqueId } from "lodash";

function formatCurrencyValue(
  inputValue: PrimitiveValueType,
): CurrencyValueType {
  if (inputValue == null || inputValue === "") {
    return {
      value: "",
      formattedValue: "",
      floatValue: undefined,
    };
  }

  const value = String(inputValue);
  const parsed = typeof inputValue === "number" ? inputValue : Number(value);
  const floatValue = Number.isFinite(parsed) ? parsed : undefined;

  return {
    value,
    formattedValue: value,
    floatValue,
  };
}

export default function RenderCurrencyInput({
  title,
  propertyKey,
  description,
  rowIndex,
  propertySubKey,
  isDisabled,
  inputValue,
  isRequired,
  evalMethod,
  error,
  message,
  isDialogField,
  selectedTabSectionKey,
  dispatch,
  rowDataReducerState,
  isInline,
}: Readonly<RenderInputProps>): JSX.Element {
  const intl = useIntl();
  const [value, setValue] = useState<CurrencyValueType>(
    formatCurrencyValue(inputValue),
  ); // preserves intermediate input such as "-"

  useEffect(() => {
    setValue(formatCurrencyValue(inputValue));
  }, [inputValue]);

  const formattedId =
    rowIndex !== undefined && propertySubKey
      ? `${propertyKey}-${rowIndex}-${propertySubKey}-input`
      : `${propertyKey}-input`;

  const handleCurrencyInput = (values: CurrencyValueType) => {
    setValue(values);
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue: values.floatValue ?? null,
        rowIndex,
        propertySubKey,
        isDialogField,
        selectedTabSectionKey,
      },
    });
  };

  useEffect(() => {
    if (evalMethod) {
      customEvaluations({
        evalMethod,
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue,
        searchResponse: undefined,
        rowDataReducerState,
        dispatch,
      });
    }
  }, [inputValue]);

  const commonProps = {
    dataTestId: formattedId,
    id: uniqueId(formattedId),
    label: title,
    name: propertyKey,
    value: value == null ? "" : value.value,
    placeholder: isDisabled ? title : intl.formatMessage(
      { id: "placeholder.enter", defaultMessage: "Enter {item}" },
      { item: title },
    ),
    onValueChange: handleCurrencyInput,
    isRequired: isRequired && !isDisabled,
    disabled: isDisabled,
    hasError: error,
    errorMessage: message,
    enableCustomValidation: true,
  };

  if (isInline) {
    // TODO: We might need to support tooltip for inline currency input in future. For now, we are not supporting it.
    return <CurrencyInput {...commonProps} hideLabel isInline />;
  }

  return <CurrencyInputWithTooltip {...commonProps} tooltip={description} />;
}
